/**
 * The segmented control: a white track holding two or more options, one of
 * which is filled navy.
 *
 * Used by the header's Pickup/Delivery switcher and by the price filter on a
 * product row. Those are meant to look identical, so the classes live here
 * rather than being copied into each, where they had already drifted apart on
 * radius, padding and weight.
 *
 * Segments must render through `<Button raw>`. The normal variants apply
 * HOVER_NAVY, which would fill an inactive segment on hover and fight the
 * states below.
 */

/**
 * Corner radius, on the track and on each option.
 *
 * The two have to stay equal: an option carrying a different radius from the
 * track either overflows its corner or leaves a gap inside it.
 */
const RADIUS = 'rounded-xl';

/** The outer track. Add a width (e.g. `w-full`) at the call site if needed. */
export const SEGMENTED_TRACK =
  `flex items-center ${RADIUS} border border-neutral-10 bg-constant-white`;

/**
 * One option. Carries the track's own radius so the selected one reads as a
 * pill rather than a squared-off bar.
 */
export const SEGMENTED_SEGMENT =
  `flex-1 ${RADIUS} px-6 py-2.5 text-sm font-semibold transition-colors duration-200 ease-out ` +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-80/30 ' +
  'disabled:cursor-wait disabled:opacity-70 select-none whitespace-nowrap';

export const SEGMENTED_ACTIVE = 'bg-blue-100 text-constant-white shadow-sm';
export const SEGMENTED_INACTIVE = 'bg-transparent text-text-secondary hover:text-blue-100';

/**
 * How tall a track ends up: 20px line box, 10px padding either side, 1px
 * border either side. For sizing controls that sit next to one, such as the
 * pager arrows and Shop all on a product row, so the row is one height.
 * Update alongside the segment padding above.
 */
export const SEGMENTED_HEIGHT = 'h-[42px]';

/** Classes for a segment in the given state. */
export function segmentClasses(active: boolean): string {
  return `${SEGMENTED_SEGMENT} ${active ? SEGMENTED_ACTIVE : SEGMENTED_INACTIVE}`;
}
